/**
 * Utility functions for exporting data and charts
 */

// CSV Export utilities
export function convertToCSV(data: any[], filename: string): void {
	if (!data || data.length === 0) {
		console.warn("No data to export");
		return;
	}

	// Get headers from the first object
	const headers = Object.keys(data[0]);

	// Create CSV content
	const csvContent = [
		// Header row
		headers.join(","),
		// Data rows
		...data.map((row) =>
			headers
				.map((header) => {
					const value = row[header];
					// Handle values that might contain commas or quotes
					if (
						typeof value === "string" &&
						(value.includes(",") || value.includes('"'))
					) {
						return `"${value.replace(/"/g, '""')}"`;
					}
					return value;
				})
				.join(","),
		),
	].join("\n");

	// Create and trigger download
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const link = document.createElement("a");
	const url = URL.createObjectURL(blob);
	link.setAttribute("href", url);
	link.setAttribute("download", `${filename}.csv`);
	link.style.visibility = "hidden";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

// Specialized CSV export functions for your data types
export function exportCategoryDataAsCSV(
	data: any[],
	propertyName: string | undefined,
	componentType: string,
): void {
	const csvData = data.map((item) => ({
		category: item.category || item.name,
		visits: item.count || item.value,
		percentage: item.percentage,
		...(item.blocked_count !== undefined && {
			blocked_count: item.blocked_count,
			allowed_count: item.allowed_count,
			blocked_percentage: item.blocked_percentage,
			allowed_percentage: item.allowed_percentage,
		}),
	}));

	const filename = propertyName
		? `${propertyName}-${componentType}-${formatDate(new Date())}`
		: `${componentType}-${formatDate(new Date())}`;

	convertToCSV(csvData, filename);
}

export function exportLocationDataAsCSV(
	data: any[],
	propertyName: string | undefined,
	componentType: string,
	level: string,
): void {
	const csvData = data.map((item) => ({
		location: item.name,
		visits: item.count,
		percentage: item.percentage,
		...(item.country_code && { country_code: item.country_code }),
		...(item.region && { region: item.region }),
		...(item.city && { city: item.city }),
	}));

	const filename = propertyName
		? `${propertyName}-${componentType}-${level}-${formatDate(new Date())}`
		: `${componentType}-${level}-${formatDate(new Date())}`;

	convertToCSV(csvData, filename);
}

export function exportTimelineDataAsCSV(
	data: any[],
	propertyName: string | undefined,
	componentType: string,
	filterInfo?: {
		timeRange?: string;
		pagePath?: string;
		aiModel?: string;
		country?: string;
		region?: string;
		city?: string;
	},
): void {
	if (!data || data.length === 0) {
		console.warn("No data to export");
		return;
	}

	// Detect if this is intent-based data by checking if the first item
	// has bot_visits field (regular timeline) or not (intent-based timeline)
	const firstItem = data[0];
	const isIntentBasedData =
		!("bot_visits" in firstItem) &&
		Object.keys(firstItem).some(
			(key) =>
				key !== "time_bucket" &&
				key !== "date_label" &&
				key !== "full_date" &&
				typeof firstItem[key] === "number",
		);

	let csvData: any[];

	if (isIntentBasedData) {
		// Intent-based timeline: export with dynamic intent columns
		// Get all intent keys (excluding metadata fields)
		const metadataFields = ["time_bucket", "date_label", "full_date"];
		const intentKeys = Object.keys(firstItem).filter(
			(key) =>
				!metadataFields.includes(key) &&
				typeof firstItem[key] === "number",
		);

		csvData = data.map((item) => {
			const row: any = {
				date: item.time_bucket || item.date_label,
			};
			// Add each intent as a column
			for (const key of intentKeys) {
				row[key] = item[key] || 0;
			}
			return row;
		});
	} else {
		// Regular timeline: bot_visits, human_visits, conversion_rate
		csvData = data.map((item) => ({
			date: item.time_bucket || item.date_label,
			bot_visits: item.bot_visits || 0,
			human_visits: item.human_visits || 0,
			conversion_rate: item.conversion_rate || 0,
		}));
	}

	const filename = propertyName
		? `${propertyName}-${componentType}-${formatDate(new Date())}`
		: `${componentType}-${formatDate(new Date())}`;

	if (filterInfo) {
		convertToCSVWithMetadata(csvData, filename, filterInfo, propertyName);
	} else {
		convertToCSV(csvData, filename);
	}
}

export function convertToCSVWithMetadata(
	data: any[],
	filename: string,
	filterInfo: {
		timeRange?: string;
		pagePath?: string;
		aiModel?: string;
		country?: string;
		region?: string;
		city?: string;
	},
	propertyName?: string,
): void {
	if (!data || data.length === 0) {
		console.warn("No data to export");
		return;
	}

	// Create metadata rows
	const metadataRows: string[] = [];
	metadataRows.push(`Export Date,${new Date().toISOString()}`);
	if (propertyName) {
		metadataRows.push(`Property,${propertyName}`);
	}

	// Add filter information
	const activeFilters: string[] = [];
	if (filterInfo.timeRange)
		activeFilters.push(`Time Range: ${filterInfo.timeRange}`);
	if (filterInfo.pagePath) activeFilters.push(`Page: ${filterInfo.pagePath}`);
	if (filterInfo.aiModel)
		activeFilters.push(`AI Model: ${filterInfo.aiModel}`);
	if (filterInfo.country)
		activeFilters.push(`Country: ${filterInfo.country}`);
	if (filterInfo.region) activeFilters.push(`Region: ${filterInfo.region}`);
	if (filterInfo.city) activeFilters.push(`City: ${filterInfo.city}`);

	if (activeFilters.length > 0) {
		metadataRows.push(`Filters Applied,"${activeFilters.join("; ")}"`);
	} else {
		metadataRows.push("Filters Applied,None");
	}

	metadataRows.push(""); // Empty row separator

	// Get headers from the first object
	const headers = Object.keys(data[0]);

	// Create CSV content with metadata
	const csvContent = [
		// Metadata rows
		...metadataRows,
		// Header row
		headers.join(","),
		// Data rows
		...data.map((row) =>
			headers
				.map((header) => {
					const value = row[header];
					// Handle values that might contain commas or quotes
					if (
						typeof value === "string" &&
						(value.includes(",") || value.includes('"'))
					) {
						return `"${value.replace(/"/g, '""')}"`;
					}
					return value;
				})
				.join(","),
		),
	].join("\n");

	// Create and trigger download
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const link = document.createElement("a");
	const url = URL.createObjectURL(blob);
	link.setAttribute("href", url);
	link.setAttribute("download", `${filename}.csv`);
	link.style.visibility = "hidden";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

// Image Export utilities
export async function exportChartAsImage(
	elementId: string,
	propertyName: string | undefined,
	componentType: string,
	format: "png" | "jpg" = "png",
): Promise<void> {
	try {
		// Dynamically import html2canvas to avoid SSR issues
		const html2canvas = await import("html2canvas-pro");

		const element = document.getElementById(elementId);
		if (!element) {
			console.error(`Element with id '${elementId}' not found`);
			return;
		}

		const canvas = await html2canvas.default(element, {
			backgroundColor: "#ffffff",
			scale: 2, // Higher resolution
			useCORS: true,
			// Never draw an image we cannot read back: a tainted canvas makes
			// toBlob throw, losing the whole export rather than one logo.
			allowTaint: false,
			logging: false,
			width: element.scrollWidth,
			height: element.scrollHeight,
		});

		// Convert to blob and download
		canvas.toBlob(
			(blob: Blob | null) => {
				if (blob) {
					const url = URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;

					const filename = propertyName
						? `${propertyName}-${componentType}-${formatDate(new Date())}.${format}`
						: `${componentType}-${formatDate(new Date())}.${format}`;

					link.download = filename;
					link.style.visibility = "hidden";
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);
				}
			},
			`image/${format}`,
			0.95,
		);
	} catch (error) {
		console.error("Failed to export chart as image:", error);
	}
}

/**
 * Copy a chart element to the clipboard as a faithful, full-color PNG image so
 * it can be pasted into Word, Google Docs, PowerPoint/Slides, etc.
 *
 * We write a single ClipboardItem with both `image/png` (the rendered chart)
 * and `text/plain` (a data fallback). We deliberately do NOT include
 * `text/html`: rich editors prefer HTML over images and would paste a table
 * instead of the chart — for charts the image must win, while the plain-text
 * representation covers plain-text targets (code editors, terminals, etc.).
 *
 * Falls back to writing just the text when the async Clipboard image API is
 * unavailable (older/locked-down browsers).
 *
 * @returns true if an image was copied, false if it fell back to text-only.
 * @throws if nothing could be written to the clipboard.
 */
export async function copyChartImageToClipboard(
	elementId: string,
	opts?: { textFallback?: string },
): Promise<boolean> {
	const element = document.getElementById(elementId);
	if (!element) {
		throw new Error(`Element with id '${elementId}' not found`);
	}

	const textFallback = opts?.textFallback ?? "";
	const canWriteImage =
		typeof ClipboardItem !== "undefined" &&
		typeof navigator !== "undefined" &&
		!!navigator.clipboard?.write;

	if (canWriteImage) {
		const html2canvas = await import("html2canvas-pro");
		const canvas = await html2canvas.default(element, {
			backgroundColor: "#ffffff",
			scale: 2, // Higher resolution
			useCORS: true,
			// See exportChartAsImage: a tainted canvas would make toBlob throw.
			allowTaint: false,
			logging: false,
			width: element.scrollWidth,
			height: element.scrollHeight,
		});

		const blob: Blob | null = await new Promise((resolve) =>
			canvas.toBlob((b) => resolve(b), "image/png", 0.95),
		);

		if (blob) {
			const items: Record<string, Blob> = {
				"image/png": blob,
			};
			if (textFallback) {
				items["text/plain"] = new Blob([textFallback], {
					type: "text/plain",
				});
			}
			await navigator.clipboard.write([new ClipboardItem(items)]);
			return true;
		}
	}

	// Fallback: text only
	if (textFallback && navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(textFallback);
		return false;
	}

	throw new Error("Clipboard image copy not supported in this browser");
}

/**
 * Copy a rich-text (HTML) representation to the clipboard so it pastes as
 * formatted content (e.g. a colored data table) into Word, Google Docs,
 * PowerPoint/Slides, etc. Writes both `text/html` and a `text/plain` fallback.
 *
 * Use this instead of {@link copyChartImageToClipboard} for charts that don't
 * rasterize cleanly (e.g. donut legends), where a faithful table beats a
 * garbled image.
 *
 * Falls back to writing plain text (HTML tags stripped) when the rich Clipboard
 * API is unavailable.
 */
export async function copyRichTextToClipboard(
	html: string,
	text?: string,
): Promise<void> {
	const plain = text ?? html.replace(/<[^>]+>/g, "");
	if (
		typeof ClipboardItem !== "undefined" &&
		typeof navigator !== "undefined" &&
		!!navigator.clipboard?.write
	) {
		await navigator.clipboard.write([
			new ClipboardItem({
				"text/html": new Blob([html], { type: "text/html" }),
				"text/plain": new Blob([plain], { type: "text/plain" }),
			}),
		]);
		return;
	}
	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(plain);
		return;
	}
	throw new Error("Clipboard not supported in this browser");
}

// SVG Export utility (for Recharts SVG elements)
export function exportSVGAsImage(
	svgElement: SVGElement,
	propertyName: string | undefined,
	componentType: string,
	format: "png" | "jpg" = "png",
): void {
	try {
		const svgData = new XMLSerializer().serializeToString(svgElement);
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");

		if (!ctx) {
			console.error("Failed to get canvas context");
			return;
		}

		const img = new Image();
		const svgBlob = new Blob([svgData], {
			type: "image/svg+xml;charset=utf-8",
		});
		const url = URL.createObjectURL(svgBlob);

		img.onload = () => {
			canvas.width = img.width;
			canvas.height = img.height;

			// Fill with white background
			ctx.fillStyle = "white";
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			ctx.drawImage(img, 0, 0);

			canvas.toBlob(
				(blob: Blob | null) => {
					if (blob) {
						const downloadUrl = URL.createObjectURL(blob);
						const link = document.createElement("a");
						link.href = downloadUrl;

						const filename = propertyName
							? `${propertyName}-${componentType}-${formatDate(new Date())}.${format}`
							: `${componentType}-${formatDate(new Date())}.${format}`;

						link.download = filename;
						link.style.visibility = "hidden";
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
						URL.revokeObjectURL(downloadUrl);
					}
				},
				`image/${format}`,
				0.95,
			);

			URL.revokeObjectURL(url);
		};

		img.onerror = (error) => {
			console.error("Failed to load SVG image:", error);
			URL.revokeObjectURL(url);
		};

		img.src = url;
	} catch (error) {
		console.error("Failed to export SVG as image:", error);
	}
}

// Helper function to format date for filenames
function formatDate(date: Date): string {
	return date.toISOString().split("T")[0];
}
